/* ============================================================================
   WORKFLOW — galeri semua jenis workflow Flow SESAAT  (ADMIN SAHAJA · 26 Okt 2026)
   ============================================================================
   Layout ikut rujukan user (gambar galeri workflow): kepala + carian · tab All/Published/Draft ·
   butang Create workflow · grid card (gambar aliran, nama, penerangan, tag, ke mana hasil pergi).

   · Data SEBENAR dalam Firestore `config/workflow_galeri` (arahan user: "aku boleh edit nama dan
     lain2, tak perlu tunggu melalui kau") — services/galeriWorkflowService.js. Klik card = popup edit.
   · Card = KATALOG sahaja; BELUM menggerakkan robot (robot dipilih oleh module + Control Panel).
   · ADMIN SAHAJA — route '/workflow' dipagar `isAdmin` dalam App.jsx DAN item menu 'workflow'
     `admin: true` dalam design/StudioShell.jsx. Buka KEDUA-DUA serentak bila sedia (+ rules baca, lihat service).
   · Teks English (pilot, keputusan user 26 Okt) → `suisBahasa={false}` (pil tanpa kamus = kawalan palsu).
     Sebelum dibuka kepada pelanggan: masuk kamus en/ms + pasang pil.
   · CSS awalan .wfg- dalam WorkflowV39.css.
   NAK PADAM: fail ini + WorkflowEditModal.jsx + GambarAliranWf.jsx + WorkflowV39.css + utils/galeriWorkflow.js
     + services/galeriWorkflowService.js + import & route '/workflow' (App.jsx) + item menu 'workflow' (StudioShell).
   ========================================================================== */
import { useEffect, useMemo, useState } from 'react';
import { Plus, Search, Pencil } from 'lucide-react';
import '../design/tokens.css';
import { PageHeader, Segmented, Btn, Field, Badge, EmptyState, IconBtn } from '../design/ui';
import GambarAliranWf from './GambarAliranWf';
import WorkflowEditModal from './WorkflowEditModal';
import './WorkflowV39.css';
import { HASIL_WF, KATEGORI_WF, tapisWorkflow, workflowKosong } from '../utils/galeriWorkflow';
import { dengarGaleriWorkflow, muatWorkflowMula } from '../services/galeriWorkflowService';

const labelHasil = Object.fromEntries(HASIL_WF.map((h) => [h.id, h.label]));
const labelKategori = Object.fromEntries(KATEGORI_WF.map((k) => [k.id, k.label]));

export default function WorkflowV39View() {
    const [data, setData] = useState(null);          // null = sedang dimuat
    const [ralatMuat, setRalatMuat] = useState('');
    const [tab, setTab] = useState('all');
    const [cari, setCari] = useState('');
    const [edit, setEdit] = useState(null);           // { wf, baru }
    const [memuatMula, setMemuatMula] = useState(false);
    const [ralatMula, setRalatMula] = useState('');

    useEffect(() => dengarGaleriWorkflow(
        (d) => { setData(d); setRalatMuat(''); },
        (e) => setRalatMuat('Could not load workflows: ' + (e?.message || e)),
    ), []);

    const senarai = useMemo(() => data?.senarai || [], [data]);
    const kiraan = useMemo(() => ({
        all: senarai.length,
        published: senarai.filter((w) => w.status === 'published').length,
        draft: senarai.filter((w) => w.status === 'draft').length,
    }), [senarai]);
    const dipapar = useMemo(() => tapisWorkflow(senarai, { tab, cari }), [senarai, tab, cari]);

    const muatMula = async () => {
        setMemuatMula(true);
        setRalatMula('');
        try { await muatWorkflowMula(); } catch (e) { setRalatMula(e?.message || String(e)); }
        setMemuatMula(false);
    };

    const ciptaBaru = () => setEdit({ wf: workflowKosong(senarai), baru: true });

    return (
        <div className="sesaat-m-tight" style={{ padding: '26px 28px 48px' }}>
            <PageHeader
                title="Workflow"
                subtitle="Every Flow workflow in SESAAT. Published = customers use it today. Draft = not ready yet."
                suisBahasa={false}
                actions={(
                    <Field icon={<Search size={15} />} placeholder="Search by name" value={cari}
                        onChange={(e) => setCari(e.target.value)} style={{ width: 240 }} />
                )}
            />

            <div className="wfg-bar">
                <Segmented value={tab} onChange={setTab} options={[
                    { value: 'all', label: `All ${kiraan.all}` },
                    { value: 'published', label: `Published ${kiraan.published}` },
                    { value: 'draft', label: `Draft ${kiraan.draft}` },
                ]} />
                <Btn icon={<Plus size={15} />} onClick={ciptaBaru} disabled={!data}>Create workflow</Btn>
            </div>

            {ralatMuat && <div className="wfg-ralat" role="alert">{ralatMuat}</div>}
            {data?.rosak > 0 && (
                <div className="wfg-ralat" role="alert">
                    {data.rosak} saved workflow(s) have invalid data and are hidden. Re-create them or ask Claude to check.
                </div>
            )}

            {!data && !ralatMuat && <p className="s-dim">Loading…</p>}

            {data && senarai.length === 0 && (
                <EmptyState
                    title="No workflows saved yet"
                    action={(
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
                            <Btn onClick={muatMula} disabled={memuatMula}>{memuatMula ? 'Loading…' : 'Load starter workflows (13)'}</Btn>
                            <Btn variant="ghost" icon={<Plus size={15} />} onClick={ciptaBaru}>Create workflow</Btn>
                        </div>
                    )}
                >
                    Start with the 13 workflows SESAAT has today (5 published, 8 draft), then edit them freely.
                    {ralatMula && <span className="wfg-ralat" style={{ display: 'block', marginTop: 10 }}>{ralatMula}</span>}
                </EmptyState>
            )}

            {data && senarai.length > 0 && dipapar.length === 0 && (
                <EmptyState title="No workflow matches">Try another tab or search word.</EmptyState>
            )}

            {dipapar.length > 0 && (
                <div className="wfg-grid">
                    {dipapar.map((w) => (
                        <div key={w.id} className="wfg-card" role="button" tabIndex={0}
                            onClick={() => setEdit({ wf: w, baru: false })}
                            onKeyDown={(e) => { if (e.key === 'Enter') setEdit({ wf: w, baru: false }); }}>
                            <div className="wfg-card__art">
                                <GambarAliranWf ilustrasi={w.ilustrasi} className="wfg-art" />
                                {w.status === 'draft' && <Badge tone="warn" className="wfg-card__status">Draft</Badge>}
                            </div>
                            <div className="wfg-card__isi">
                                <h3 className="wfg-card__nama">{w.nama}</h3>
                                {w.penerangan && <p className="wfg-card__desc">{w.penerangan}</p>}
                                <div className="wfg-card__tags">
                                    <span className="wfg-tag wfg-tag--kat">{labelKategori[w.kategori]}</span>
                                    {w.tag.map((t) => <span key={t} className="wfg-tag">{t}</span>)}
                                </div>
                            </div>
                            <div className="wfg-card__kaki">
                                <div className="wfg-card__hasil">
                                    {w.hasil.map((h) => <span key={h} className="wfg-pill wfg-pill--kecil">{labelHasil[h]}</span>)}
                                </div>
                                <IconBtn icon={<Pencil size={14} />} label="Edit"
                                    onClick={(e) => { e.stopPropagation(); setEdit({ wf: w, baru: false }); }} />
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {edit && (
                <WorkflowEditModal key={edit.wf.id} awal={edit.wf} baru={edit.baru} onTutup={() => setEdit(null)} />
            )}
        </div>
    );
}
